import { RouteContext } from '@contexts/RouteContext';
import { appErrorSelector } from '@store/selectors/app';
import { configLoadedSelector, definitionsSelector } from '@store/selectors/config';
import { sendHeightMessage } from '@utils/postMessage';
import React, {
  Suspense, useEffect, lazy, useCallback, useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Routes, Route, useLocation, useNavigate,
} from 'react-router-dom';

import { usePrevious } from '@ezugi/hooks/src/usePrevious';

import { locale } from './store/locale';
import { ui } from './store/ui';

const Home = lazy(() => import('./pages/Home'));
const Error = lazy(() => import('./pages/Error'));

const rootElement = document.getElementById('root');

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const previousHeight = usePrevious(
    rootElement?.firstElementChild?.scrollHeight,
  );
  const navigate = useNavigate();

  const { basename, setBasename } = useContext(RouteContext);
  const definitions = useSelector(definitionsSelector);
  const configLoaded = useSelector(configLoadedSelector);
  const { code } = useSelector(appErrorSelector) || {};

  // Set basename if necessary & redirect to it
  useEffect(() => {
    if (!!definitions?.LINK_PREFIX && basename !== definitions?.LINK_PREFIX) {
      setBasename(definitions.LINK_PREFIX);
      navigate(`${definitions.LINK_PREFIX}/${window.location.href}`);
    }
  }, [ definitions, basename, setBasename, navigate ]);

  const toggleLocalization = useCallback(() => {
    dispatch(locale.toggle());
  }, [ dispatch ]);

  useEffect(() => {
    window.TOGGLE_LOCALIZATION = toggleLocalization;
  }, [ toggleLocalization ]);

  // Close InfoPage on navigation change
  useEffect(() => {
    dispatch(ui.resetPageInfo());
  }, [ dispatch, location ]);

  // Send Height PostMessage on route change
  useEffect(() => {
    const currentHeight = rootElement?.firstElementChild?.scrollHeight;

    if (previousHeight !== currentHeight) {
      sendHeightMessage(currentHeight);
    }
  }, [ previousHeight, location ]);

  if (!configLoaded && !code) return null;

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route
          path="/error/:code"
          element={<Error />}
        />
        <Route
          path="/*"
          element={<Home />}
        />
      </Routes>
    </Suspense>
  );
};

export default App;
